import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import BrandedPlayer from "@/components/BrandedPlayer";
import CloudflareHlsPlayer from "@/components/CloudflareHlsPlayer";
import { NotFound, Forbidden } from "@/pages/ErrorPages";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function Watch() {
  const { contentSlug, season, episode } = useParams();
  const { slug, sp } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, status, logout } = useAuth();
  const [tenantContent, setTenantContent] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [accessChecked, setAccessChecked] = useState(false);
  const [progressInfo, setProgressInfo] = useState({ position: 0 });

  // Phase 8 — `?t=` query param overrides saved progress (used by Up Next "Resume from"
  // and the prior_in_progress secondary card to deep-link straight to a position).
  const tParam = parseInt(searchParams.get("t") || "0", 10);

  // Wait for auth to resolve
  useEffect(() => {
    let alive = true;
    setPageStatus("loading");
    api.get(`/public/tenants/${slug}/content/${contentSlug}`)
      .then((r) => alive && (setTenantContent(r.data), setPageStatus("ready")))
      .catch(() => alive && setPageStatus("not_found"));
    return () => { alive = false; };
  }, [slug, contentSlug]);

  // Resolve initial position from /api/me/progress when authed
  useEffect(() => {
    if (!tenantContent?.content?.id || status !== "authenticated") return;
    api.get(`/me/progress/${tenantContent.content.id}`)
      .then((r) => {
        const rows = r.data.rows || [];
        if (season && episode) {
          const epDoc = (tenantContent.content.episodes || []).find(
            (e) => String(e.season_number) === String(season).replace(/^s/i, "") &&
                   String(e.episode_number) === String(episode).replace(/^e/i, ""),
          );
          if (epDoc) {
            const row = rows.find((r2) => r2.episode_id === epDoc.id);
            if (row) setProgressInfo({ position: row.position_seconds });
          }
        } else {
          const row = rows.find((r2) => !r2.episode_id);
          if (row) setProgressInfo({ position: row.position_seconds });
        }
      })
      .catch(() => {});
  }, [tenantContent?.content?.id, status, season, episode]);

  // Phase 3 entitlement gate — call /api/me/watch-access; on 402 redirect to detail with ?upgrade=1
  useEffect(() => {
    if (!tenantContent?.content?.id || status !== "authenticated") {
      setAccessChecked(true);
      return;
    }
    setAccessChecked(false);
    api.get(`/me/watch-access/${tenantContent.content.id}`)
      .then(() => setAccessChecked(true))
      .catch((e) => {
        if (e.response?.status === 402) {
          navigate(sp(`/title/${contentSlug}?upgrade=1`), { replace: true });
        } else if (e.response?.status === 403) {
          navigate("/forbidden", { replace: true });
        } else {
          setAccessChecked(true);
        }
      });
  }, [tenantContent?.content?.id, status, slug, contentSlug, navigate]);

  if (pageStatus === "loading" || status === "loading" || !accessChecked) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] grid place-items-center text-white/40 text-sm uppercase tracking-widest" data-testid="watch-loading">
        Loading…
      </div>
    );
  }
  if (pageStatus === "not_found" || !tenantContent) return <NotFound to={sp(`/home`)} label="Back to home" message="This title isn't available to watch right now." />;

  const tenant = tenantContent.tenant;
  const content = tenantContent.content;

  // Auth gate
  if (status !== "authenticated" || !user) {
    const next = location.pathname + location.search;
    return <Navigate to={sp(`/login?next=${encodeURIComponent(next)}`)} replace />;
  }
  // Tenant scope
  if (user.role === "subscriber" && user.tenant_id !== tenant.id) return <Forbidden />;
  if (user.role === "tenant_admin" && user.tenant_id !== tenant.id) return <Forbidden />;

  // Resolve which video to play
  let videoSource = null;
  let episodeId = null;
  let episodeLabel = null;
  let crumb = content.title;

  if (content.kind === "series") {
    const eps = [...(content.episodes || [])].sort(
      (a, b) => (a.season_number - b.season_number) || (a.episode_number - b.episode_number),
    );
    let ep;
    if (season && episode) {
      const seasonNum = parseInt(String(season).replace(/^s/i, ""), 10);
      const episodeNum = parseInt(String(episode).replace(/^e/i, ""), 10);
      ep = eps.find((e) => e.season_number === seasonNum && e.episode_number === episodeNum);
      if (!ep) return <NotFound to={sp(`/home`)} label="Back to home" message="We couldn't find that episode." />;
    } else {
      // No episode specified (e.g. hero/detail "Play") — start from the first episode.
      ep = eps[0];
      if (!ep) return <NotFound to={sp(`/title/${contentSlug}`)} label="View title" message="This series doesn't have any episodes yet." />;
    }
    videoSource = ep.video_source;
    episodeId = ep.id;
    episodeLabel = `S${ep.season_number}E${ep.episode_number}`;
    crumb = `${content.title} · ${episodeLabel} — ${ep.title}`;
  } else {
    if (!content.video_source) return <NotFound to={sp(`/home`)} label="Back to home" message="This title doesn't have a video yet." />;
    videoSource = content.video_source;
  }

  if (!videoSource) return <NotFound to={sp(`/title/${contentSlug}`)} label="View title" message="This episode doesn't have a video yet." />;

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8" data-testid="watch-page">
        <Link
          to={sp(`/title/${contentSlug}`)}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/60 hover:text-white"
          data-testid="watch-back-link"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to details
        </Link>
        <h1 className="mt-4 font-display font-bold text-2xl md:text-3xl tracking-tight" data-testid="watch-title">{crumb}</h1>
        <div className="mt-6">
          {videoSource?.type === "cloudflare" ? (
            <CloudflareHlsPlayer
              tenant={tenant}
              tenantSlug={slug}
              content={content}
              episodeId={episodeId}
              videoSource={videoSource}
              initialPositionSeconds={tParam > 0 ? tParam : progressInfo.position}
            />
          ) : (
            <BrandedPlayer
              tenant={tenant}
              tenantSlug={slug}
              content={content}
              episodeId={episodeId}
              episodeLabel={episodeLabel}
              videoSource={videoSource}
              initialPositionSeconds={tParam > 0 ? tParam : progressInfo.position}
            />
          )}
        </div>
        <div className="mt-6 max-w-3xl">
          <p className="text-white/65 font-light leading-relaxed">{content.description}</p>
          {(content.genres || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {content.genres.map((g) => (
                <span key={g} className="text-[10px] uppercase tracking-[0.25em] px-2 py-1 bg-white/5 border border-white/10 text-white/70">{g}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </StorefrontLayout>
  );
}

// Local Navigate wrapper to avoid extra import at top
function Navigate({ to, replace }) {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}
